import React from "react";
import PropTypes from "prop-types";
import { Box, Divider, Menu, Tooltip, Typography } from "@mui/material";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

import { Button } from "./Button";
import { useTheme as useCustomTheme } from "../theme/useTheme";

export const SortSelectorPopover = ({
  sortDirection,
  sortField,
  onSortDirectionChange,
  onSortFieldChange,
  directionOptions,
  fieldOptions,
  showDirection = true,
}) => {
  const { neutrals, theme } = useCustomTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isOpen = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Ordenar" arrow>
        <Button
          variant="outline"
          size="sm"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            borderColor: neutrals.gray[200],
            color: neutrals.gray[600],
            flexShrink: 0,
            minWidth: "32px",
            height: "32px",
            padding: "4px",
            borderRadius: "999px",
            boxShadow: "none",
          }}
          aria-label="Ordenar oportunidades"
        >
          <ArrowUpDown size={14} />
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 0.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "10px",
            boxShadow: theme.shadows[4],
            width: "fit-content",
            minWidth: 0,
            maxWidth: "calc(100vw - 24px)",
            pr: 1.5,
          },
        }}
      >
        <Box sx={{ py: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
              display: "block",
              px: 1.5,
              pb: 0.5,
            }}
          >
            Ordenar por
          </Typography>
          <Box sx={{ px: 0.5 }}>
            {fieldOptions.map((option) => {
              const isSelected = option.value === sortField;
              return (
                <Box
                  key={option.value}
                  onClick={() => onSortFieldChange(option.value)}
                  sx={{
                    height: 38,
                    px: 1,
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: `1.25px solid ${
                        isSelected ? theme.palette.text.primary : theme.palette.text.secondary
                      }`,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {isSelected && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.text.primary,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {showDirection && (
            <>
              <Divider sx={{ my: 1, mx: 0 }} />

              <Box sx={{ px: 0.5 }}>
                <Box
                  onClick={() => onSortDirectionChange("asc")}
                  sx={{
                    height: 38,
                    px: 1,
                    borderRadius: "10px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: sortDirection === "asc" ? theme.palette.grey[100] : "transparent",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ArrowUp size={16} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      color: theme.palette.text.primary,
                      fontWeight: sortDirection === "asc" ? 500 : 400,
                    }}
                  >
                    {directionOptions.find((option) => option.value === "asc")?.label || "Ascendente"}
                  </Typography>
                </Box>

                <Box
                  onClick={() => onSortDirectionChange("desc")}
                  sx={{
                    height: 38,
                    px: 1,
                    mt: 0.5,
                    borderRadius: "10px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: sortDirection === "desc" ? theme.palette.grey[100] : "transparent",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ArrowDown size={16} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      color: theme.palette.text.primary,
                      fontWeight: sortDirection === "desc" ? 500 : 400,
                    }}
                  >
                    {directionOptions.find((option) => option.value === "desc")?.label || "Descendente"}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Menu>
    </>
  );
};

SortSelectorPopover.propTypes = {
  sortDirection: PropTypes.oneOf(["asc", "desc"]),
  sortField: PropTypes.string.isRequired,
  onSortDirectionChange: PropTypes.func,
  onSortFieldChange: PropTypes.func.isRequired,
  directionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  fieldOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  showDirection: PropTypes.bool,
};
